import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';

interface StatusData {
  active: number;
  completed: number;
  total: number;
}

interface AssignmentStatusChartProps {
  data: StatusData;
  available: number;
}

export const AssignmentStatusChart: React.FC<AssignmentStatusChartProps> = ({ data, available }) => {
  const activePercentage = data.total > 0 ? (data.active / data.total) * 100 : 0;
  const completedPercentage = data.total > 0 ? (data.completed / data.total) * 100 : 0;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Ionicons name="pie-chart" size={20} color={theme.colors.primary} />
        <Text style={styles.title}>Fleet Status Overview</Text>
      </View>

      {/* Visual Bars */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBar}>
          <View 
            style={[
              styles.progressSegment,
              { 
                width: `${activePercentage}%`,
                backgroundColor: theme.colors.green600 
              }
            ]} 
          />
          <View 
            style={[
              styles.progressSegment,
              { 
                width: `${completedPercentage}%`,
                backgroundColor: theme.colors.gray300 
              }
            ]} 
          />
        </View>
      </View>

      {/* Stats Grid */}
      <View style={styles.statsGrid}>
        <View style={styles.statItem}>
          <View style={[styles.statDot, { backgroundColor: theme.colors.green600 }]} />
          <View>
            <Text style={styles.statLabel}>Active Now</Text>
            <Text style={styles.statValue}>{data.active}</Text>
          </View>
        </View>

        <View style={styles.statItem}>
          <View style={[styles.statDot, { backgroundColor: theme.colors.primary }]} />
          <View>
            <Text style={styles.statLabel}>Available</Text>
            <Text style={styles.statValue}>{available}</Text>
          </View>
        </View>

        <View style={styles.statItem}>
          <View style={[styles.statDot, { backgroundColor: theme.colors.gray300 }]} />
          <View>
            <Text style={styles.statLabel}>Completed Today</Text>
            <Text style={styles.statValue}>{data.completed}</Text>
          </View>
        </View>
      </View>

      {/* Availability Indicator */}
      <View style={[
        styles.availabilityBanner,
        { 
          backgroundColor: available >= 10 
            ? theme.colors.green600 
            : available >= 5 
              ? '#ff9800' 
              : theme.colors.red600 
        }
      ]}>
        <Ionicons 
          name={available >= 10 ? 'checkmark-circle' : available >= 5 ? 'warning' : 'alert-circle'} 
          size={16} 
          color={theme.colors.white} 
        />
        <Text style={styles.availabilityText}>
          {available >= 10 
            ? 'Good Availability' 
            : available >= 5 
              ? 'Low Availability' 
              : 'Critical: Low Stock'}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    ...theme.shadows.md,
    marginBottom: theme.spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  title: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.xs,
  },
  progressContainer: {
    marginBottom: theme.spacing.md,
  },
  progressBar: {
    height: 8,
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.sm,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  progressSegment: {
    height: '100%',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.md,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  statDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  statLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
  },
  statValue: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
  },
  availabilityBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  availabilityText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as '600',
    color: theme.colors.white,
  },
});
